import { useEffect, useState } from 'react'

import Button from 'common/components/button'
import Modal from 'common/components/Modal'
import RadialProgress from 'common/components/RadialProgress'
import { useAppSelector } from 'common/hooks/redux'
import {
  selectHasMigratedAccountEditAccess,
  selectMigratedRedirectUrl,
} from 'features/auth/store/authSlice'

import { messages as bannerMessages } from './MigratedAccountBanner'

export const REDIRECT_SECONDS = 10

export const messages = {
  heading: bannerMessages.migratedHeading,
  staffText: `Please make any changes there. ${bannerMessages.editAccessNote}`,
  redirectText: (seconds: number) =>
    `Taking you there in ${seconds} second${seconds === 1 ? '' : 's'}.`,
  goNow: 'Go now',
  close: 'Close',
}

// Customer users are sent to the new platform after a countdown; staff can
// close it and keep working (PHX-2785)
const MigratedAccountModal = () => {
  const redirectUrl = useAppSelector(selectMigratedRedirectUrl)
  const hasEditAccess = useAppSelector(selectHasMigratedAccountEditAccess)
  const { accountId } = useAppSelector((state) => state.authStates)
  const [dismissedAccountId, setDismissedAccountId] = useState<string>()
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS)
  const isRedirecting = !!redirectUrl && !hasEditAccess

  useEffect(() => {
    if (!isRedirecting) {
      return
    }
    setSecondsLeft(REDIRECT_SECONDS)
    const timer = setInterval(
      () => setSecondsLeft((seconds) => Math.max(seconds - 1, 0)),
      1000
    )
    return () => clearInterval(timer)
  }, [isRedirecting, redirectUrl])

  useEffect(() => {
    if (isRedirecting && secondsLeft === 0) {
      window.location.href = redirectUrl
    }
  }, [isRedirecting, redirectUrl, secondsLeft])

  if (!redirectUrl || dismissedAccountId === accountId) {
    return null
  }

  const goNow = () => {
    window.location.href = redirectUrl
  }

  return (
    <Modal
      visible
      title={messages.heading}
      closable={hasEditAccess}
      onClose={
        hasEditAccess ? () => setDismissedAccountId(accountId) : undefined
      }
      footer={
        <div className="flex gap-4">
          {hasEditAccess && (
            <Button
              variant="secondary"
              onClick={() => setDismissedAccountId(accountId)}
            >
              {messages.close}
            </Button>
          )}
          <Button variant="primary" onClick={goNow}>
            {hasEditAccess ? bannerMessages.cta : messages.goNow}
          </Button>
        </div>
      }
    >
      {hasEditAccess ? (
        <p className="text-base-700">{messages.staffText}</p>
      ) : (
        <div className="flex items-center gap-3 text-base-700">
          <RadialProgress
            size="sm"
            value={((REDIRECT_SECONDS - secondsLeft) / REDIRECT_SECONDS) * 100}
          />
          <p>{messages.redirectText(secondsLeft)}</p>
        </div>
      )}
    </Modal>
  )
}

MigratedAccountModal.displayName = 'MigratedAccountModal'

export default MigratedAccountModal
